<template>
  <div
    class="owl-compat owl-carousel owl-theme"
    :id="id || undefined"
    @mouseenter="pause"
    @mouseleave="resume"
  >
    <div class="owl-compat__viewport owl-wrapper-outer">
      <div ref="track" class="owl-compat__track" :style="trackStyle">
        <slot />
      </div>
    </div>
    <button
      v-if="nav"
      type="button"
      class="owl-compat__nav owl-compat__nav--prev"
      @click="prev"
    >
      ‹
    </button>
    <button
      v-if="nav"
      type="button"
      class="owl-compat__nav owl-compat__nav--next"
      @click="next"
    >
      ›
    </button>
    <div v-if="dots" class="owl-compat__dots owl-dots">
      <button
        v-for="n in pageCount"
        :key="n"
        type="button"
        class="owl-dot"
        :class="{ active: current === n - 1 }"
        @click="go(n - 1)"
      >
        <span />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "OwlCarouselCompat",
  props: {
    id: { type: String, default: "" },
    items: { type: [Number, String], default: 1 },
    nav: { type: Boolean, default: false },
    dots: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: false },
    autoplayTimeout: { type: Number, default: 5000 },
    margin: { type: [Number, String], default: 0 },
    loop: { type: Boolean, default: true },
    responsive: { type: Object, default: null },
    navText: { type: Array, default: null },
  },
  data() {
    return {
      current: 0,
      slideCount: 0,
      timer: null,
      resizeItems: null,
    };
  },
  computed: {
    perPage() {
      if (this.resizeItems != null) return this.resizeItems;
      return Math.max(1, Number(this.items) || 1);
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.slideCount / this.perPage));
    },
    trackStyle() {
      return {
        transform: `translateX(-${this.current * (100 / this.perPage)}%)`,
        gap: `${this.margin}px`,
      };
    },
  },
  watch: {
    perPage() {
      this.applySlideSizes();
      if (this.current > this.pageCount - 1) {
        this.current = Math.max(0, this.pageCount - 1);
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.refresh();
      this.updateResponsive();
      if (this.autoplay) this.resume();
    });
    window.addEventListener("resize", this.updateResponsive, { passive: true });
  },
  updated() {
    const track = this.$refs.track;
    const count = track ? track.children.length : 0;
    if (count !== this.slideCount) {
      this.$nextTick(() => this.refresh());
    }
  },
  beforeUnmount() {
    this.pause();
    window.removeEventListener("resize", this.updateResponsive);
  },
  methods: {
    refresh() {
      this.measureSlides();
      this.applySlideSizes();
    },
    measureSlides() {
      const track = this.$refs.track;
      if (!track) return;
      this.slideCount = track.children.length;
    },
    applySlideSizes() {
      const track = this.$refs.track;
      if (!track) return;
      const width = `${100 / this.perPage}%`;
      Array.from(track.children).forEach((child) => {
        child.classList.add("owl-compat__slide");
        child.style.flex = `0 0 ${width}`;
        child.style.maxWidth = width;
        child.style.boxSizing = "border-box";
      });
    },
    updateResponsive() {
      if (!this.responsive) {
        this.resizeItems = null;
        return;
      }
      const width = window.innerWidth;
      const breakpoints = Object.keys(this.responsive)
        .map(Number)
        .sort((a, b) => a - b);
      let matched = Number(this.items) || 1;
      for (const bp of breakpoints) {
        if (width >= bp) {
          const conf = this.responsive[bp];
          if (conf && conf.items != null) matched = Number(conf.items) || 1;
        }
      }
      this.resizeItems = matched;
      this.$nextTick(() => this.refresh());
    },
    go(index) {
      this.current = index;
    },
    next() {
      if (this.current >= this.pageCount - 1) {
        this.current = this.loop ? 0 : this.current;
      } else {
        this.current += 1;
      }
    },
    prev() {
      if (this.current <= 0) {
        this.current = this.loop ? this.pageCount - 1 : 0;
      } else {
        this.current -= 1;
      }
    },
    pause() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    resume() {
      this.pause();
      if (!this.autoplay) return;
      this.timer = setInterval(() => this.next(), this.autoplayTimeout);
    },
  },
};
</script>

<style scoped>
.owl-compat {
  position: relative;
  overflow: hidden;
  width: 100%;
}
.owl-compat__viewport {
  overflow: hidden;
  width: 100%;
  border-radius: 5px;
}
.owl-compat__track {
  display: flex;
  transition: transform 0.45s ease;
  width: 100%;
}
.owl-compat__track > :deep(*) {
  min-width: 0;
}
.owl-compat__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  border: none;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
}
.owl-compat__nav--prev {
  left: 8px;
}
.owl-compat__nav--next {
  right: 8px;
}
/* Match legacy owl-theme dots: overlaid on the slide image */
.owl-compat__dots {
  position: absolute !important;
  z-index: 3;
  bottom: 30px;
  right: 30px;
  left: auto;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 0;
  margin: 0;
  width: auto;
}
.owl-compat__dots .owl-dot {
  width: auto;
  height: auto;
  border: none;
  background: transparent;
  padding: 5px 4px;
  cursor: pointer;
  outline: none;
  box-shadow: none;
}
.owl-compat__dots .owl-dot span {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #fff;
  opacity: 0.45;
}
.owl-compat__dots .owl-dot.active span {
  opacity: 1;
  background: #fff;
}
@media screen and (max-width: 600px) {
  .owl-compat__dots {
    bottom: 5px;
    right: 10px;
  }
}
</style>
